import { z } from 'zod';

/** A qué se adjunta un archivo. */
export const ATTACHMENT_ENTITIES = ['LEAD', 'CONTACT', 'DEAL', 'UNIT', 'PROJECT'] as const;
export type AttachmentEntity = (typeof ATTACHMENT_ENTITIES)[number];
export const ATTACHMENT_ENTITY_LABELS: Record<AttachmentEntity, string> = {
  LEAD: 'Lead',
  CONTACT: 'Cliente',
  DEAL: 'Proceso',
  UNIT: 'Unidad',
  PROJECT: 'Proyecto',
};

export const ATTACHMENT_CATEGORIES = [
  'PLANO',
  'FOTO',
  'BROCHURE',
  'VOUCHER',
  'DNI',
  'CONTRATO',
  'OTRO',
] as const;
export type AttachmentCategory = (typeof ATTACHMENT_CATEGORIES)[number];
export const ATTACHMENT_CATEGORY_LABELS: Record<AttachmentCategory, string> = {
  PLANO: 'Plano',
  FOTO: 'Foto',
  BROCHURE: 'Brochure',
  VOUCHER: 'Voucher de pago',
  DNI: 'DNI / documento',
  CONTRATO: 'Contrato / minuta',
  OTRO: 'Otro',
};

/** Categorías en el orden que se ofrecen según a qué se adjunta (la primera es la sugerida). */
export const ATTACHMENT_CATEGORIES_BY_ENTITY: Record<AttachmentEntity, AttachmentCategory[]> = {
  UNIT: ['PLANO', 'FOTO', 'OTRO'],
  PROJECT: ['BROCHURE', 'PLANO', 'FOTO', 'OTRO'],
  CONTACT: ['DNI', 'OTRO'],
  LEAD: ['VOUCHER', 'DNI', 'OTRO'],
  DEAL: ['VOUCHER', 'CONTRATO', 'DNI', 'OTRO'],
};

/** Tamaño máximo por archivo. */
export const ATTACHMENT_MAX_MB = 15;
export const ATTACHMENT_MAX_BYTES = ATTACHMENT_MAX_MB * 1024 * 1024;

/** Tipos permitidos: tipo MIME → extensión con la que se guarda en disco. */
export const ATTACHMENT_MIME_TYPES: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/msword': 'doc',
  'application/vnd.ms-excel': 'xls',
};
/** Extensiones aceptadas, para mensajes y para el atributo `accept` del selector de archivos. */
export const ATTACHMENT_EXTENSIONS = Array.from(new Set(Object.values(ATTACHMENT_MIME_TYPES)));
export const ATTACHMENT_ACCEPT = [
  ...Object.keys(ATTACHMENT_MIME_TYPES),
  ...ATTACHMENT_EXTENSIONS.map((e) => `.${e}`),
  '.jpeg',
].join(',');

/**
 * Resuelve el tipo MIME y la extensión con la que se guarda un archivo. Se confía en el tipo que declara el
 * navegador; si no declara ninguno (o uno genérico), se usa la extensión del nombre. Devuelve null si no está permitido.
 */
export function resolveAttachmentType(
  fileName: string,
  mimeType: string | null | undefined,
): { contentType: string; extension: string } | null {
  const mime = (mimeType ?? '').toLowerCase().split(';')[0]?.trim() ?? '';
  const byMime = ATTACHMENT_MIME_TYPES[mime];
  if (byMime) return { contentType: mime, extension: byMime };
  if (mime && mime !== 'application/octet-stream') return null;
  const ext = /\.([a-z0-9]+)$/.exec(fileName.toLowerCase())?.[1];
  if (!ext) return null;
  const normalized = ext === 'jpeg' ? 'jpg' : ext;
  const entry = Object.entries(ATTACHMENT_MIME_TYPES).find(([, e]) => e === normalized);
  return entry ? { contentType: entry[0], extension: entry[1] } : null;
}

export function isImageType(contentType: string): boolean {
  return contentType.startsWith('image/');
}

/** "812 KB", "2.4 MB". */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
}

/** Campos que acompañan al archivo en el formulario multipart. */
export const attachmentFieldsSchema = z.object({
  entityType: z.enum(ATTACHMENT_ENTITIES),
  entityId: z.string().uuid(),
  category: z.enum(ATTACHMENT_CATEGORIES).default('OTRO'),
  description: z
    .string()
    .trim()
    .max(300)
    .optional()
    .transform((v) => (v && v.length ? v : null)),
});
export type AttachmentFields = z.infer<typeof attachmentFieldsSchema>;

export const attachmentListQuerySchema = z.object({
  entityType: z.enum(ATTACHMENT_ENTITIES),
  entityId: z.string().uuid(),
});

export interface AttachmentDto {
  id: string;
  entityType: AttachmentEntity;
  entityId: string;
  category: AttachmentCategory;
  fileName: string;
  contentType: string;
  size: number;
  description: string | null;
  isImage: boolean;
  uploadedBy: { id: string; name: string } | null;
  createdAt: string;
  /** Enlace firmado para ver el archivo; vence a los 30 minutos y se renueva al recargar la lista. */
  url: string;
  /** Mismo enlace, pero fuerza la descarga. */
  downloadUrl: string;
  /** Si el usuario actual puede borrarlo (quien lo subió o quien gestiona la empresa). */
  canDelete: boolean;
}

/** Vigencia de los enlaces firmados de archivos (la lista los emite al pedirla). */
export const ATTACHMENT_URL_TTL_SECONDS = 30 * 60;
/**
 * A partir de cuántos segundos de emitido conviene pedir un enlace nuevo antes de abrirlo (G2, ux-ficha-13): una ficha
 * abierta toda la mañana no debe mandar al asesor a un enlace vencido.
 */
export const ATTACHMENT_URL_REFRESH_AFTER_SECONDS = 20 * 60;

/** Vencimiento (`exp`, segundos Unix) de un enlace firmado de archivo; null si no lo trae. */
export function attachmentUrlExpiresAt(url: string): number | null {
  const m = /[?&]exp=(\d+)/.exec(url);
  if (!m) return null;
  const exp = Number(m[1]);
  return Number.isFinite(exp) ? exp : null;
}

/** true si el enlace tiene más de `ATTACHMENT_URL_REFRESH_AFTER_SECONDS` de emitido (o ya venció / no se entiende). */
export function attachmentUrlNeedsRefresh(url: string, now = Date.now()): boolean {
  const exp = attachmentUrlExpiresAt(url);
  if (exp === null) return true;
  const issuedAt = exp - ATTACHMENT_URL_TTL_SECONDS;
  return now / 1000 - issuedAt >= ATTACHMENT_URL_REFRESH_AFTER_SECONDS;
}
